import { useState } from "react";
import { X } from "lucide-react";
import { colorForUser, initialsOf } from "@/lib/avatarUtils";

/**
 * MonteurMehrfachAuswahl – Auswahl mehrerer Monteure im Stil des Bausteins AuswahlFeld (A-027 S1).
 *
 * AuswahlFeld selbst ist an die Textvorlagen-Listen gebunden (ein Wert) und passt hier nicht:
 * die Monteure kommen aus den Benutzern und es sind mehrere. Darum dasselbe Aussehen und Verhalten:
 *   • Auswahlliste (natives <select>, am Handy gut bedienbar) mit dem Eintrag „+ Monteur hinzufügen …“,
 *   • bei langen Listen ein Suchfeld darüber,
 *   • gewählte Monteure als Schilder mit „×“ (Reihenfolge bleibt; der Erste ist wie bisher der Hauptmonteur).
 * Die Liste der Monteure pflegt Ralph in der Benutzerverwaltung – deshalb hier kein „+ Neu“.
 *
 * Props: mitarbeiter [{username, anzeige_name}], value [username], onChange(neueListe), label, testId, hinweis
 */
const SUCHE_AB = 8;

export default function MonteurMehrfachAuswahl({ mitarbeiter = [], value = [], onChange, label = "Monteure", testId = "auswahl-monteure", hinweis = null }) {
  const [suche, setSuche] = useState("");
  const name = (u) => mitarbeiter.find((m) => m.username === u)?.anzeige_name || u;
  const frei = mitarbeiter.filter((m) => !value.includes(m.username));
  const q = suche.trim().toLowerCase();
  const gefiltert = q ? frei.filter((m) => (m.anzeige_name || m.username).toLowerCase().includes(q) || m.username.toLowerCase().includes(q)) : frei;
  const hinzu = (u) => { if (u) { onChange?.([...value, u]); setSuche(""); } };
  const weg = (u) => onChange?.(value.filter((x) => x !== u));

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium mb-1.5" htmlFor={`${testId}-select`}>{label}</label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`${testId}-gewaehlt`}>
          {value.map((u, i) => {
            const c = colorForUser(u);
            return (
              <span key={u} className="inline-flex items-center gap-1.5 pl-1 pr-1 py-0.5 rounded-full border border-input bg-muted/40 text-sm" data-testid={`${testId}-chip-${u}`}>
                <span className={`w-5 h-5 rounded-full ${c?.bg || "bg-slate-400"} text-white text-[10px] font-bold flex items-center justify-center`}>{initialsOf(u)}</span>
                <span>{name(u)}</span>
                {i === 0 && value.length > 1 && <span className="text-[10px] text-muted-foreground">(Haupt)</span>}
                <button type="button" onClick={() => weg(u)} className="p-0.5 rounded-full hover:bg-muted" title={`${name(u)} entfernen`} aria-label={`${name(u)} entfernen`} data-testid={`${testId}-weg-${u}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {frei.length > SUCHE_AB && (
        <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Monteur suchen …"
          className="mb-1.5 h-8 w-full rounded-sm border border-input bg-background px-2 text-xs" data-testid={`${testId}-suche`} />
      )}
      <select
        id={`${testId}-select`}
        value=""
        onChange={(e) => hinzu(e.target.value)}
        disabled={frei.length === 0}
        className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm disabled:opacity-60"
        data-testid={`${testId}-select`}
      >
        <option value="">{frei.length === 0 ? (value.length ? "— alle sind gewählt —" : "— keine Benutzer vorhanden —") : value.length ? "+ weiteren Monteur hinzufügen …" : "+ Monteur hinzufügen …"}</option>
        {gefiltert.map((m) => <option key={m.username} value={m.username}>{m.anzeige_name || m.username}</option>)}
      </select>
      {hinweis}
    </div>
  );
}
